import { canonicalJson } from '../model/canonical-json.js';
import { formPages, layoutOf, navigationMode } from './form-layout.js';
import { personEntryFindings, personTemplateFindings } from './person-field.js';
const fr = (label, fallback) => label?.['fr'] ?? (label === undefined ? undefined : Object.values(label)[0]) ?? fallback;
/** Racines des références d'une expression (`$answers.x` → `answers`), littéraux exclus. */
function roots(expression) {
    const found = new Set();
    const walk = (node) => {
        if (typeof node === 'string') {
            const match = /^\$([a-z][a-z0-9_]*)/.exec(node);
            if (match !== null)
                found.add(match[1]);
        }
        else if (Array.isArray(node))
            node.forEach(walk);
        else if (node !== null && typeof node === 'object') {
            for (const [key, value] of Object.entries(node))
                if (key !== '$literal')
                    walk(value);
        }
    };
    walk(expression);
    return [...found];
}
function strings(value, pointer, out) {
    if (typeof value === 'string')
        out.push({ pointer, text: value });
    else if (Array.isArray(value))
        value.forEach((item, index) => strings(item, `${pointer}/${index}`, out));
    else if (value !== null && typeof value === 'object')
        for (const [key, item] of Object.entries(value))
            strings(item, `${pointer}/${key}`, out);
}
const escapePointer = (key) => key.replace(/~/g, '~0').replace(/\//g, '~1');
/** Feuilles d'une valeur, par pointeur JSON ; un tableau est une feuille entière. */
export function settingLeaves(value, pointer, out = new Map()) {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        for (const [key, item] of Object.entries(value))
            settingLeaves(item, `${pointer}/${escapePointer(key)}`, out);
    }
    else if (value !== undefined)
        out.set(pointer, canonicalJson(value));
    return out;
}
const ENTRY_KEYS = ['presentation', 'target', 'evidence', 'warnings', 'duplicates', 'required_when', 'readonly_when', 'visible_when', 'validation_severity', 'initial_value'];
/** Réglages d'un préréglage, projetés sur la forme d'une entrée de champ (sans l'identifiant du champ). */
export function presetProjection(preset) {
    const { field_id: _ignored, ...field } = preset.field;
    void _ignored;
    const leaves = settingLeaves(field, '/field');
    const entry = (preset.entry ?? {});
    for (const key of ENTRY_KEYS)
        if (entry[key] !== undefined)
            settingLeaves(entry[key], `/${key}`, leaves);
    if (!leaves.has('/presentation/widget'))
        leaves.set('/presentation/widget', canonicalJson(preset.widget));
    return leaves;
}
/** Réglages d'une entrée de champ comparables à son préréglage. */
function entryProjection(entry) {
    const { field_id: _ignored, ...field } = (entry['field'] ?? {});
    void _ignored;
    const leaves = settingLeaves(field, '/field');
    for (const key of ENTRY_KEYS)
        if (entry[key] !== undefined)
            settingLeaves(entry[key], `/${key}`, leaves);
    return leaves;
}
const covered = (pointer, overrides) => overrides.some((override) => pointer === override || pointer.startsWith(`${override}/`));
/** Réglages qui diffèrent entre un préréglage et une entrée, hors surcharges déclarées. */
export function presetDrift(preset, entry, overrides) {
    const expected = presetProjection(preset);
    const actual = entryProjection(entry);
    const drift = new Set();
    for (const [pointer, value] of expected)
        if (actual.get(pointer) !== value && !covered(pointer, overrides))
            drift.add(pointer);
    for (const [pointer, value] of actual)
        if (expected.get(pointer) !== value && !covered(pointer, overrides))
            drift.add(pointer);
    return [...drift].sort();
}
// 1.15.0 (ADR-152) : le champ « Personne » admet aussi le bouton.
const BUTTON_WIDGETS = new Set(['selector', 'date', 'datetime', 'time', 'scan_code', 'scan_doc', 'lot', 'photo', 'person']);
const DERIVED_ROOTS = new Set(['answers', 'inputs', 'form']);
const MESSAGE_ROOTS = new Set(['answers', 'inputs', 'form', 'aggregates']);
const LOCALIZED_KEYS = new Set(['label', 'description', 'message', 'title', 'content', 'placeholder', 'overline', 'next_label', 'submit_label', 'confirm_label', 'cancel_label', 'in_series_message', 'alt', 'prompt', 'text', 'eyebrow']);
const LOCALE = /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/;
function effectiveWidget(entry) {
    const widget = entry['presentation']?.['widget'];
    if (typeof widget === 'string')
        return widget;
    const type = entry['field']?.['type'];
    if (type === 'reference')
        return 'selector';
    if (type === 'date')
        return 'date';
    return undefined;
}
function choiceValues(field) {
    if (field['type'] === 'boolean')
        return ['true', 'false'];
    if (field['type'] !== 'enumeration')
        return undefined;
    return field['validation']?.['enum_values'] ?? [];
}
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const SEVERITY_KEYS = { range: ['min', 'max'], length: ['min_length', 'max_length'], pattern: ['pattern'] };
/** La valeur fixe convient-elle au type du champ ? (inspection-template-body 1.13.0, `initial_value`). */
function initialValueFits(field, value) {
    switch (field['type']) {
        case 'number':
        case 'decimal':
            return typeof value === 'number' && Number.isFinite(value);
        case 'string':
        case 'rich_text':
            return typeof value === 'string';
        case 'boolean':
            return typeof value === 'boolean';
        case 'enumeration':
            return typeof value === 'string' && (choiceValues(field) ?? []).includes(value);
        case 'date':
            return typeof value === 'string' && DATE.test(value);
        default:
            return false;
    }
}
/**
 * Contrôles d'un champ (ADR-146 addendum (23)) : la « Valeur au départ » fixe est du type du champ et n'est
 * jamais posée sur un champ calculé ; une sévérité « avertir » vise une validation déclarée.
 */
function controlFindings(entry, pointer, label) {
    const findings = [];
    const field = (entry['field'] ?? {});
    const initial = entry['initial_value'];
    if (initial !== undefined) {
        if (entry['compute'] !== undefined)
            findings.push({ path: `${pointer}/initial_value`, code: 'INITIAL_VALUE_COMPUTED', message: `« ${label} » est calculé par le serveur : il n’a pas de valeur au départ. Retirez-la.`, severity: 'error' });
        else if (initial.source === 'fixed' && !initialValueFits(field, initial.value))
            findings.push({ path: `${pointer}/initial_value/value`, code: 'INITIAL_VALUE_INVALID', message: `La valeur au départ de « ${label} » ne convient pas à ce champ. Choisissez une valeur du bon type, ou « Vide ».`, severity: 'error' });
        else if (initial.source === 'last_entry' && (field['type'] === 'file' || entry['evidence'] !== undefined))
            findings.push({ path: `${pointer}/initial_value`, code: 'INITIAL_VALUE_INVALID', message: `« ${label} » attend une photo ou une preuve : la dernière saisie ne se reprend pas. Choisissez « Vide ».`, severity: 'error' });
    }
    const severity = entry['validation_severity'];
    const validation = (field['validation'] ?? {});
    for (const [rule, mode] of Object.entries(severity ?? {})) {
        if (mode === 'warn' && !(SEVERITY_KEYS[rule] ?? []).some((key) => validation[key] !== undefined)) {
            findings.push({ path: `${pointer}/validation_severity/${rule}`, code: 'VALIDATION_SEVERITY_UNUSED', message: `« ${label} » avertit pour une validation qui n’existe pas : elle n’aura aucun effet.`, severity: 'warning' });
        }
    }
    return findings;
}
/**
 * Images par valeur d'énumération d'un champ (field-definition 1.7.0) : seulement des valeurs connues ;
 * 1.8.0 : sur une énumération extensible, une image peut anticiper la valeur qu'un sous-Pack ajoutera.
 */
export function enumImageFindings(field, pointer) {
    const images = field['validation']?.['enum_images'];
    if (images === undefined || field['validation']['enum_extensible'] === true)
        return [];
    const values = new Set(field['validation']['enum_values'] ?? []);
    const label = fr(field['label'], 'sans nom');
    return Object.keys(images)
        .filter((value) => !values.has(value))
        .map((value) => ({ path: `${pointer}/validation/enum_images/${escapePointer(value)}`, code: 'ENUM_IMAGE_VALUE_UNKNOWN', message: `Une image est attachée à une valeur que « ${label} » ne propose pas. Retirez cette image ou ajoutez la valeur.`, severity: 'error', details: { value } }));
}
/** Réglages de présentation d'une entrée de champ (ou de l'entrée d'un préréglage). */
function presentationFindings(entry, pointer, label) {
    const findings = [];
    const presentation = entry['presentation'];
    const field = (entry['field'] ?? {});
    const error = (path, code, message, details) => {
        findings.push({ path, code, message, severity: 'error', ...(details === undefined ? {} : { details }) });
    };
    const warn = (path, code, message, details) => {
        findings.push({ path, code, message, severity: 'warning', ...(details === undefined ? {} : { details }) });
    };
    findings.push(...enumImageFindings(field, `${pointer}/field`));
    // ADR-152 : champ « Personne » (source, famille, valeur au départ, puce).
    findings.push(...personEntryFindings(entry, pointer, label));
    if (presentation === undefined)
        return findings;
    const widget = effectiveWidget(entry);
    const at = `${pointer}/presentation`;
    if (presentation['variant'] === 'button' && (widget === undefined || !BUTTON_WIDGETS.has(widget))) {
        error(`${at}/variant`, 'PRESENTATION_VARIANT_UNSUPPORTED', `« ${label} » ne peut pas s’afficher en bouton. Gardez l’affichage en champ.`, { widget });
    }
    const values = choiceValues(field);
    const options = presentation['options'];
    if (options !== undefined) {
        if (values === undefined)
            error(`${at}/options`, 'OPTIONS_NOT_A_CHOICE', `« ${label} » n’est pas une liste de choix : ses options ne peuvent pas avoir d’image ni de couleur.`);
        else
            for (const value of Object.keys(options))
                if (!values.includes(value))
                    error(`${at}/options/${escapePointer(value)}`, 'OPTION_VALUE_UNKNOWN', `Un réglage vise un choix que « ${label} » ne propose pas. Retirez-le.`, { value });
    }
    const visual = presentation['option_visual'];
    if (visual === 'object_image' && field['type'] !== 'enumeration' && field['type'] !== 'reference') {
        error(`${at}/option_visual`, 'OPTION_OBJECT_IMAGE_NO_SOURCE', `« ${label} » ne vient ni d’une liste du modèle ni d’un objet : il n’a pas d’image d’objet. Choisissez une image par choix ou une icône.`);
    }
    if (presentation['option_source'] !== undefined && field['type'] !== 'enumeration') {
        error(`${at}/option_source`, 'OPTION_SOURCE_NOT_A_CHOICE', `Seule une liste de choix peut reprendre ses choix d’une donnée du modèle. « ${label} » n’en est pas une.`);
    }
    const optionEntries = Object.values(options ?? {});
    if (visual === 'image' && !optionEntries.some((option) => option['image'] !== undefined))
        warn(`${at}/option_visual`, 'OPTION_IMAGE_MISSING', `Aucun choix de « ${label} » n’a d’image : les libellés s’afficheront seuls.`);
    if (visual === 'dot' && !optionEntries.some((option) => option['color'] !== undefined))
        warn(`${at}/option_visual`, 'OPTION_COLOR_MISSING', `Aucun choix de « ${label} » n’a de pastille de couleur.`);
    if (presentation['tiles'] !== undefined && widget !== 'radio_tiles')
        warn(`${at}/tiles`, 'PRESENTATION_SETTING_IGNORED', `Les réglages des tuiles ne servent pas : « ${label} » ne s’affiche pas en tuiles.`);
    if (presentation['segmented'] !== undefined && widget !== 'segmented')
        warn(`${at}/segmented`, 'PRESENTATION_SETTING_IGNORED', `Les réglages du choix segmenté ne servent pas : « ${label} » ne s’affiche pas en segments.`);
    if (widget === 'segmented') {
        if (values === undefined)
            error(`${at}/widget`, 'SEGMENTED_NOT_A_CHOICE', `Le choix segmenté ne s’utilise qu’avec une liste de choix ou un oui / non. « ${label} » n’en est pas un.`);
        else if (values.length > 4)
            warn(`${at}/widget`, 'SEGMENTED_TOO_MANY', `« ${label} » propose plus de quatre choix : des tuiles ou une liste se liront mieux.`, { count: values.length });
    }
    if (presentation['empty_display'] === 'placeholder' && presentation['placeholder'] === undefined)
        warn(`${at}/placeholder`, 'PLACEHOLDER_MISSING', `« ${label} » affiche un texte indicatif quand il est vide, mais ce texte n’est pas écrit.`);
    return findings;
}
/** Préréglage de champ (kind `field_preset`) : cohérence propre, avant toute utilisation. */
export function checkFieldPreset(preset) {
    const label = fr(preset.label, 'sans nom');
    const entry = { field: preset.field, ...(preset.entry ?? {}) };
    const findings = presentationFindings(entry, '/entry', label).map((finding) => ({ ...finding, path: finding.path.replace(/^\/entry\/field/, '/field') }));
    const widget = (preset.entry?.presentation ?? {})['widget'];
    if (widget !== undefined && widget !== preset.widget)
        findings.push({ path: '/entry/presentation/widget', code: 'PRESET_WIDGET_MISMATCH', message: `Le préréglage « ${label} » annonce un widget et en règle un autre. Gardez le même.`, severity: 'error' });
    if (preset.availability?.scope === 'listed_forms' && (preset.availability.forms ?? []).length === 0)
        findings.push({ path: '/availability/forms', code: 'PRESET_FORMS_MISSING', message: `Le préréglage « ${label} » est réservé à certains formulaires, mais aucun n’est choisi.`, severity: 'error' });
    return findings;
}
/** Index des champs d'un gabarit, par « partie.champ ». */
function fieldsOf(template) {
    const fields = new Map();
    template.sections.forEach((section, sectionIndex) => {
        section.fields.forEach((entry, fieldIndex) => {
            const field = entry.field;
            fields.set(`${section.section_id}.${String(field['field_id'])}`, {
                section_id: section.section_id,
                field_id: String(field['field_id']),
                label: fr(field['label'], String(field['field_id'])),
                list: section.series !== undefined || section.repeat !== undefined,
                entry: entry,
                field,
                pointer: `/sections/${sectionIndex}/fields/${fieldIndex}`,
            });
        });
    });
    return fields;
}
const refKey = (ref) => (ref === undefined ? undefined : `${ref.section_id}.${ref.field_id}`);
/**
 * Vérifie les ajouts 1.10.0 d'un gabarit de formulaire. Un gabarit qui n'en déclare aucun ne produit
 * aucun constat. Les avertissements (`severity: warning`) ne refusent rien.
 */
export function checkFormEditor(template, context = {}) {
    const findings = [];
    const error = (path, code, message, details) => {
        findings.push({ path, code, message, severity: 'error', ...(details === undefined ? {} : { details }) });
    };
    const warn = (path, code, message, details) => {
        findings.push({ path, code, message, severity: 'warning', ...(details === undefined ? {} : { details }) });
    };
    const fields = fieldsOf(template);
    const sections = new Map(template.sections.map((section) => [section.section_id, section]));
    const sectionName = (id) => fr(sections.get(id)?.label, 'sans nom');
    const pages = formPages(template);
    const layout = layoutOf(template);
    // ADR-152 : rôles connus des couches actives, reprise du nom (`echo.into`).
    findings.push(...personTemplateFindings(template, context.personRoles));
    // ---- Champs : présentation, visuels, contrôles, préréglages.
    for (const info of fields.values()) {
        findings.push(...presentationFindings(info.entry, info.pointer, info.label));
        findings.push(...controlFindings(info.entry, info.pointer, info.label));
        const preset = info.entry['preset'];
        if (preset === undefined || context.presets === undefined || !context.presets.has(preset.ref))
            continue;
        const body = context.presets.get(preset.ref);
        if (body === null || body === undefined) {
            error(`${info.pointer}/preset/ref`, 'PRESET_UNKNOWN', `« ${info.label} » vient d’un préréglage qui n’existe pas ou n’est plus publié. Détachez le champ ou choisissez un autre préréglage.`, { ref: preset.ref });
            continue;
        }
        const drift = presetDrift(body, info.entry, preset.overrides ?? []);
        if (drift.length > 0)
            error(`${info.pointer}/preset`, 'PRESET_DRIFT', `« ${info.label} » ne suit plus son préréglage « ${fr(body.label, 'sans nom')} » pour un réglage qui n’est pas marqué « modifié ici ». Revenez au préréglage, ou marquez ce réglage comme modifié ici.`, { ref: preset.ref, settings: drift });
        const known = new Set([...presetProjection(body).keys(), ...entryProjection(info.entry).keys()]);
        for (const override of preset.overrides ?? []) {
            if (![...known].some((pointer) => pointer === override || pointer.startsWith(`${override}/`)))
                warn(`${info.pointer}/preset/overrides`, 'PRESET_OVERRIDE_UNKNOWN', `« ${info.label} » marque comme modifié ici un réglage qui n’existe pas.`, { override });
        }
    }
    // ---- Réactions.
    const reactions = (template.reactions ?? []);
    const reactionIds = new Set();
    const writers = new Map();
    const stateEffects = new Set();
    const edges = new Map();
    reactions.forEach((reaction, index) => {
        const pointer = `/reactions/${index}`;
        const name = fr(reaction['label'], `réaction ${index + 1}`);
        const id = String(reaction['reaction_id']);
        if (reactionIds.has(id))
            error(`${pointer}/reaction_id`, 'REACTION_DUPLICATE', `Deux réactions portent le même nom interne (« ${name} »). Renommez l’une d’elles.`, { reaction_id: id });
        reactionIds.add(id);
        const texts = [];
        strings(reaction, pointer, texts);
        for (const entry of texts)
            if (entry.text.includes('{{'))
                error(entry.pointer, 'REACTION_TEMPLATE_STRING_FORBIDDEN', 'Un texte contient des accolades doubles : écrivez le texte tel qu’il doit s’afficher, ou insérez une valeur du formulaire depuis la liste.', { text: entry.text });
        const trigger = reaction['trigger'];
        const triggerKey = refKey(trigger.field);
        // Partie à lignes du déclencheur (changement d'un champ de liste, ajout de ligne) ; `undefined` : la fiche.
        let group;
        if (trigger.on === 'change') {
            const source = triggerKey === undefined ? undefined : fields.get(triggerKey);
            if (source === undefined)
                error(`${pointer}/trigger/field`, 'REACTION_FIELD_UNKNOWN', `La réaction « ${name} » se déclenche sur un champ qui n’existe plus. Choisissez un champ de la liste.`, { field: triggerKey });
            else if (source.list)
                group = source.section_id;
        }
        if (trigger.on === 'row_add') {
            const section = trigger.section_id === undefined ? undefined : sections.get(trigger.section_id);
            if (section === undefined || (section.series === undefined && section.repeat === undefined))
                error(`${pointer}/trigger/section_id`, 'REACTION_ROW_SECTION_NOT_LIST', `La réaction « ${name} » attend l’ajout d’une ligne dans une partie qui n’est pas une liste de lignes. Choisissez une liste.`, { section_id: trigger.section_id });
            else
                group = section.section_id;
        }
        if (trigger.on === 'page_validate' && trigger.page_id !== undefined && !pages.some((page) => page.page_id === trigger.page_id)) {
            error(`${pointer}/trigger/page_id`, 'REACTION_PAGE_UNKNOWN', `La réaction « ${name} » attend la validation d’une page qui n’existe plus. Choisissez une page de la liste.`, { page_id: trigger.page_id });
        }
        (reaction['actions'] ?? []).forEach((action, actionIndex) => {
            const at = `${pointer}/actions/${actionIndex}`;
            const kind = action['action'];
            const targetKey = refKey(action['target']);
            const target = targetKey === undefined ? undefined : fields.get(targetKey);
            if (targetKey !== undefined && target === undefined) {
                error(`${at}/target`, 'REACTION_FIELD_UNKNOWN', `Une action de la réaction « ${name} » vise un champ qui n’existe plus. Choisissez un champ de la liste.`, { field: targetKey });
            }
            // Racines des expressions : ce que le serveur sait rejouer, et les totaux pour un message.
            const allowed = kind === 'message' ? MESSAGE_ROOTS : DERIVED_ROOTS;
            for (const key of ['when', 'value', 'options', 'text', 'filter']) {
                for (const root of roots(action[key])) {
                    if (!allowed.has(root))
                        error(`${at}/${key}`, 'REACTION_EXPRESSION_ROOT', `Une action de la réaction « ${name} » lit une donnée qui n’est pas disponible ici. Choisissez une valeur du formulaire dans la liste.`, { root });
                }
            }
            if ((kind === 'state' || kind === 'reload') && trigger.on !== 'change' && trigger.on !== 'open') {
                error(`${at}/action`, 'REACTION_DERIVED_TRIGGER', `« Afficher, masquer, obligatoire » et « Recharger une liste » se déclenchent au changement d’une valeur ou à l’ouverture du formulaire. Changez le moment de la réaction « ${name} ».`, { trigger: trigger.on });
            }
            if (kind === 'set' && target !== undefined) {
                if (target.entry['compute'] !== undefined)
                    error(`${at}/target`, 'REACTION_SET_COMPUTED', `« ${target.label} » est calculé par le serveur : une réaction ne peut pas le remplir.`);
                // Addendum (16), point 3 confirmé : toute la fiche hors listes ; une ligne ne remplit que sa propre ligne.
                const sameGroup = group === undefined ? !target.list : target.section_id === group;
                if (!sameGroup)
                    error(`${at}/target`, 'REACTION_SET_OUTSIDE_GROUP', group === undefined ? `La réaction « ${name} » remplit « ${target.label} », qui est dans une liste de lignes : une réaction de la fiche ne remplit pas une ligne. Choisissez un champ hors des listes.` : `La réaction « ${name} » remplit « ${target.label} », qui n’est pas dans la même ligne que ce qui la déclenche. Choisissez un champ de « ${sectionName(group)} ».`, { group, target: targetKey });
                if (writers.has(targetKey))
                    error(`${at}/target`, 'REACTION_MULTIPLE_WRITERS', `« ${target.label} » est déjà rempli par la réaction « ${writers.get(targetKey)} ». Un champ n’est rempli que par une seule réaction.`, { target: targetKey });
                else
                    writers.set(targetKey, name);
                if (trigger.on === 'change' && triggerKey !== undefined) {
                    if (!edges.has(triggerKey))
                        edges.set(triggerKey, new Set());
                    edges.get(triggerKey).add(targetKey);
                }
            }
            // Une ligne ne règle que sa propre ligne ; la fiche règle la fiche et toutes les lignes (ADR-146 addendum (17)).
            if ((kind === 'state' || kind === 'reload') && target !== undefined && group !== undefined && target.section_id !== group) {
                error(`${at}/target`, 'REACTION_STATE_OUTSIDE_ROW', `La réaction « ${name} » part d’une ligne de « ${sectionName(group)} » et règle « ${target.label} », qui n’est pas dans cette ligne. Choisissez un champ de la même ligne, ou déclenchez la réaction depuis la fiche.`, { group, target: targetKey });
            }
            if (kind === 'state' && group !== undefined && action['target_page'] !== undefined) {
                error(`${at}/target_page`, 'REACTION_STATE_OUTSIDE_ROW', `La réaction « ${name} » part d’une ligne : elle ne peut pas afficher ou masquer une page. Déclenchez-la depuis la fiche.`, { group });
            }
            if (kind === 'state') {
                const page = action['target_page'];
                if (page !== undefined && !pages.some((candidate) => candidate.page_id === page))
                    error(`${at}/target_page`, 'REACTION_PAGE_UNKNOWN', `La réaction « ${name} » vise une page qui n’existe plus. Choisissez une page de la liste.`, { page_id: page });
                if (page !== undefined && action['effect'] !== 'show' && action['effect'] !== 'hide')
                    error(`${at}/effect`, 'REACTION_PAGE_EFFECT', 'Une page s’affiche ou se masque ; elle ne se rend ni obligatoire ni en lecture seule.');
                const key = `${targetKey ?? `page:${page}`}#${String(action['effect'])}`;
                if (stateEffects.has(key))
                    error(`${at}/effect`, 'REACTION_STATE_CONFLICT', `Deux actions règlent déjà le même état de « ${target?.label ?? page} ». Réunissez-les en une seule, avec une condition.`, { target: key });
                stateEffects.add(key);
            }
            if (kind === 'reload' && target !== undefined) {
                if (action['filter'] !== undefined && target.field['type'] !== 'reference')
                    error(`${at}/filter`, 'REACTION_RELOAD_NOT_SELECTOR', `« ${target.label} » n’est pas une liste d’objets à choisir : sa liste ne se filtre pas.`);
                if (action['options'] !== undefined) {
                    const values = choiceValues(target.field);
                    if (target.field['type'] !== 'enumeration' || values === undefined)
                        error(`${at}/options`, 'REACTION_RELOAD_NOT_ENUMERATION', `« ${target.label} » n’est pas une liste de choix : ses choix ne se restreignent pas.`);
                    else if (Array.isArray(action['options'])) {
                        for (const value of action['options'])
                            if (typeof value === 'string' && !value.startsWith('$') && !values.includes(value))
                                error(`${at}/options`, 'REACTION_OPTION_UNKNOWN', `Un choix proposé n’existe pas dans « ${target.label} ». Retirez-le.`, { value });
                    }
                }
            }
            if (kind === 'message' && action['kind'] === 'confirm' && trigger.on === 'open') {
                error(`${at}/kind`, 'REACTION_CONFIRM_ON_OPEN', 'Une confirmation demande à l’opérateur de revenir sur un geste : elle ne s’utilise pas à l’ouverture du formulaire. Choisissez un message.');
            }
        });
    });
    // Cascade « remplit » : sans cycle, quatre niveaux au plus (ADR-144 §5).
    const visiting = [];
    const done = new Set();
    let cycle;
    let depth = 0;
    const visit = (node) => {
        if (cycle !== undefined)
            return 0;
        const at = visiting.indexOf(node);
        if (at >= 0) {
            cycle = [...visiting.slice(at), node];
            return 0;
        }
        if (done.has(node) && !edges.has(node))
            return 0;
        visiting.push(node);
        let longest = 0;
        for (const next of edges.get(node) ?? [])
            longest = Math.max(longest, 1 + visit(next));
        visiting.pop();
        done.add(node);
        return longest;
    };
    for (const node of edges.keys())
        depth = Math.max(depth, visit(node));
    if (cycle !== undefined) {
        const labels = cycle.map((key) => fields.get(key)?.label ?? key);
        error('/reactions', 'REACTION_CYCLE', `Des réactions se remplissent en boucle : ${labels.map((label) => `« ${label} »`).join(' → ')}. Retirez l’une d’elles.`, { cycle });
    }
    else if (depth > 4) {
        error('/reactions', 'REACTION_CHAIN_TOO_DEEP', 'Des réactions s’enchaînent sur plus de quatre niveaux. Raccourcissez la chaîne.', { depth });
    }
    // ---- Bandeau et pied.
    if (layout !== undefined) {
        const header = layout.header;
        const footer = (layout.footer ?? {});
        const shortcut = (header?.['submit_shortcut'] ?? {});
        const show = shortcut['show'] ?? 'always';
        const mode = navigationMode(template, 'landscape');
        if (shortcut['appearance'] === 'primary' && show !== 'never' && footer['hidden'] !== true && shortcut['next_style'] !== 'demote') {
            error('/layout/header/submit_shortcut', 'LAYOUT_PRIMARY_ACTION_MULTIPLE', '« Valider » du bandeau et « Suivant » de la barre du bas seraient tous deux des boutons principaux. Faites passer « Suivant » en secondaire, ou choisissez une autre apparence pour « Valider ».');
        }
        if (footer['hidden'] === true && show !== 'always' && mode !== 'steps_with_summary') {
            error('/layout/footer/hidden', 'LAYOUT_SUBMIT_UNREACHABLE', 'La barre du bas est masquée et le bandeau n’affiche pas toujours « Valider » : la fiche ne pourrait pas être validée. Affichez « Valider » dans le bandeau, ou la barre du bas.');
        }
        if (footer['hidden'] === true)
            warn('/layout/footer/hidden', 'LAYOUT_FOOTER_HIDDEN', 'La barre du bas est masquée : « Précédent » et « Suivant » ne s’afficheront pas.');
        for (const key of ['date', 'operator']) {
            if (header?.[key] === 'none')
                warn(`/layout/header/${key}`, 'LAYOUT_HEADER_ELEMENT_HIDDEN', key === 'date' ? 'La date de la fiche est masquée dans le bandeau.' : 'L’opérateur est masqué dans le bandeau.');
        }
        // ---- Placements : puces, colonnes, liste et détail.
        const aggregates = new Set((layout.aggregates ?? []).map((aggregate) => aggregate.aggregate_id));
        for (const [id, raw] of Object.entries(layout.placements ?? {})) {
            const placement = raw;
            const section = sections.get(id);
            if (section === undefined)
                continue;
            const at = `/layout/placements/${id}`;
            const isList = section.series !== undefined || section.repeat !== undefined;
            const columns = new Set(section.fields.map((entry) => String(entry.field['field_id'])));
            const chips = (placement['chips'] ?? []);
            if (chips.length > 0 && !isList)
                error(`${at}/chips`, 'LAYOUT_CHIPS_NOT_LIST', `Des compteurs sont affichés sur « ${sectionName(id)} », qui n’est pas une liste de lignes.`);
            chips.forEach((chip, index) => {
                if (!aggregates.has(chip.aggregate_ref))
                    error(`${at}/chips/${index}`, 'LAYOUT_CHIP_AGGREGATE_UNKNOWN', `Un compteur de « ${sectionName(id)} » cite un total qui n’existe pas. Choisissez un total de la liste.`, { aggregate_ref: chip.aggregate_ref });
            });
            for (const column of Object.keys((placement['column_widths'] ?? {}))) {
                if (!columns.has(column))
                    error(`${at}/column_widths`, 'LAYOUT_COLUMN_UNKNOWN', `Une largeur de colonne vise une colonne qui n’existe pas dans « ${sectionName(id)} ».`, { field_id: column });
            }
            // 1.12.0 (ADR-146 addendum (21)) : libellé court d'en-tête de colonne.
            for (const column of Object.keys((placement['column_labels'] ?? {}))) {
                if (!columns.has(column))
                    error(`${at}/column_labels`, 'LAYOUT_COLUMN_UNKNOWN', `Un libellé de colonne vise une colonne qui n’existe pas dans « ${sectionName(id)} ».`, { field_id: column });
            }
            // 1.14.0 (ADR-146 addendum (26)) : onglets, recherche et bouton rapide d'une liste.
            for (const key of ['tabs', 'search', 'row_action', 'add_label']) {
                if (placement[key] !== undefined && !isList)
                    error(`${at}/${key}`, 'LAYOUT_LIST_SETTING_NOT_LIST', `« ${sectionName(id)} » n’est pas une liste de lignes : ses onglets, sa recherche et son bouton rapide ne s’afficheraient pas.`);
            }
            for (const field of (placement['search']?.['fields'] ?? [])) {
                if (!columns.has(field))
                    error(`${at}/search/fields`, 'LAYOUT_COLUMN_UNKNOWN', `La recherche de « ${sectionName(id)} » porte sur une colonne qui n’existe pas.`, { field_id: field });
            }
            const quick = placement['row_action'];
            for (const [field, value] of Object.entries(quick?.values ?? {})) {
                const entry = section.fields.find((candidate) => String(candidate.field['field_id']) === field);
                if (entry === undefined) {
                    error(`${at}/row_action/values`, 'LAYOUT_COLUMN_UNKNOWN', `Le bouton rapide de « ${sectionName(id)} » remplit un champ qui n’existe pas dans la ligne.`, { field_id: field });
                    continue;
                }
                const definition = entry.field;
                const admitted = definition.type === 'enumeration' ? typeof value === 'string' && (definition.validation?.enum_values ?? []).includes(value)
                    : definition.type === 'boolean' ? typeof value === 'boolean'
                        : definition.type === 'number' ? typeof value === 'number'
                            : typeof value === 'string';
                if (!admitted)
                    error(`${at}/row_action/values/${field}`, 'LAYOUT_ROW_ACTION_VALUE_INVALID', `Le bouton rapide de « ${sectionName(id)} » pose une valeur que le champ « ${field} » n’admet pas.`, { field_id: field });
            }
            const panel = placement['entry_panel'];
            const heading = panel?.['heading_field'];
            if (heading !== undefined && !columns.has(heading))
                error(`${at}/entry_panel/heading_field`, 'LAYOUT_COLUMN_UNKNOWN', `Le titre du détail montre une colonne qui n’existe pas dans « ${sectionName(id)} ».`, { field_id: heading });
            if (placement['presentation']?.['as'] === 'list_detail' && panel === undefined) {
                error(`${at}/presentation`, 'LAYOUT_LIST_DETAIL_PANEL_MISSING', `« ${sectionName(id)} » s’affiche en liste et détail : choisissez la zone de son détail.`);
            }
            // Ligne en cours (1.16.0, ADR-146 addendum (28)) : seulement une liste et détail, dont le bas ajoute une ligne.
            if (panel?.['pending_row'] !== undefined && (placement['presentation']?.['as'] !== 'list_detail' || panel['next_button'] === undefined)) {
                error(`${at}/entry_panel/pending_row`, 'LAYOUT_PENDING_ROW_UNSUPPORTED', `« ${sectionName(id)} » montre une ligne « en cours » : il faut une liste et détail avec un bouton d’ajout en bas du détail.`);
            }
        }
    }
    // ---- Traductions : suggestions à relire, langues incomplètes. Jamais bloquant.
    findings.push(...translationFindings(template));
    return findings;
}
function resolvePointer(root, pointer) {
    let node = root;
    for (const raw of pointer.split('/').slice(1)) {
        const key = raw.replace(/~1/g, '/').replace(/~0/g, '~');
        if (node === null || typeof node !== 'object')
            return undefined;
        node = node[key];
    }
    return node;
}
/** Textes localisés d'un corps, par pointeur : clés usuelles de libellé et libellés des valeurs. */
function localizedTexts(body) {
    const out = [];
    const isLocalized = (value) => value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0 && Object.entries(value).every(([key, item]) => LOCALE.test(key) && typeof item === 'string');
    const walk = (node, pointer, parent) => {
        if (node === null || typeof node !== 'object')
            return;
        if (Array.isArray(node)) {
            node.forEach((item, index) => walk(item, `${pointer}/${index}`, parent));
            return;
        }
        for (const [key, value] of Object.entries(node)) {
            const at = `${pointer}/${escapePointer(key)}`;
            if ((LOCALIZED_KEYS.has(key) || parent === 'enum_labels') && isLocalized(value))
                out.push({ pointer: at, text: value });
            else if (key !== 'translation_suggestions')
                walk(value, at, key);
        }
    };
    walk(body, '', undefined);
    return out;
}
/** Traductions suggérées non relues et langues incomplètes (décision de l'utilisateur du 2026-10-21). */
export function translationFindings(body) {
    const findings = [];
    const suggestions = (body['translation_suggestions'] ?? []);
    let pending = 0;
    suggestions.forEach((suggestion, index) => {
        const text = resolvePointer(body, suggestion.pointer);
        if (text === null || typeof text !== 'object' || typeof text[suggestion.locale] !== 'string') {
            findings.push({ path: `/translation_suggestions/${index}`, code: 'TRANSLATION_SUGGESTION_STALE', message: 'Une traduction suggérée ne correspond plus à aucun texte : elle sera ignorée.', severity: 'warning', details: { pointer: suggestion.pointer, locale: suggestion.locale } });
        }
        else
            pending += 1;
    });
    if (pending > 0)
        findings.push({ path: '/translation_suggestions', code: 'TRANSLATION_SUGGESTIONS_TO_REVIEW', message: `${pending} traduction${pending > 1 ? 's' : ''} automatique${pending > 1 ? 's' : ''} à relire. La fiche peut être publiée : elles s’afficheront telles quelles.`, severity: 'warning', details: { count: pending } });
    const texts = localizedTexts(body);
    const locales = new Set(texts.flatMap((entry) => Object.keys(entry.text)));
    for (const locale of [...locales].sort()) {
        const missing = texts.filter((entry) => entry.text[locale] === undefined).length;
        if (missing > 0)
            findings.push({ path: '', code: 'TRANSLATION_INCOMPLETE', message: `${missing} texte${missing > 1 ? 's' : ''} sans traduction (${locale}) : la langue par défaut s’affichera à la place.`, severity: 'warning', details: { locale, missing } });
    }
    return findings;
}
/**
 * Usages d'un préréglage dans des gabarits, et, avec un corps envisagé, l'effet du changement : un usage
 * suit les réglages changés qu'il ne surcharge pas, et garde ceux qu'il surcharge (« 3 suivent · 2 gardent »).
 */
export function presetUsages(templates, 
/** Le préréglage, ou ses identifiants (le préréglage du client et celui du Pack qu'il remplace, field-preset-body 1.1.0). */
presetId, change) {
    const changed = change === undefined ? [] : (() => {
        const before = presetProjection(change.current);
        const after = presetProjection(change.proposed);
        return [...new Set([...before.keys(), ...after.keys()])].filter((pointer) => before.get(pointer) !== after.get(pointer)).sort();
    })();
    const usages = [];
    for (const template of templates) {
        for (const section of template.body.sections) {
            for (const entry of section.fields) {
                const preset = entry['preset'];
                if (preset === undefined)
                    continue;
                const at = preset.ref.lastIndexOf('@');
                if (!(typeof presetId === 'string' ? [presetId] : presetId).includes(preset.ref.slice(0, at)))
                    continue;
                const overrides = preset.overrides ?? [];
                const field = entry.field;
                const usage = {
                    template_id: template.artifact_id,
                    template_version: template.artifact_version,
                    template_label: template.body.label,
                    status: template.status,
                    section_id: section.section_id,
                    field_id: String(field['field_id']),
                    element_label: { fr: `${fr(section.label, section.section_id)} › ${fr(field['label'], String(field['field_id']))}` },
                    cited_version: preset.ref.slice(at + 1),
                    overrides,
                };
                if (change !== undefined) {
                    const kept = changed.filter((pointer) => covered(pointer, overrides));
                    usage.kept = kept;
                    usage.outcome = changed.length === 0 ? 'unchanged' : kept.length === changed.length ? 'keeps' : 'follows';
                }
                usages.push(usage);
            }
        }
    }
    return usages;
}
// ---- Propagation d'un préréglage (ADR-146 addendum (16) décision 4, addendum (23)).
const unescapePointer = (segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~');
function valueAtPointer(root, pointer) {
    let node = root;
    for (const segment of pointer.split('/').slice(1).map(unescapePointer)) {
        if (node === null || typeof node !== 'object')
            return undefined;
        node = node[segment];
    }
    return node;
}
function writeAtPointer(root, pointer, value) {
    const segments = pointer.split('/').slice(1).map(unescapePointer);
    let node = root;
    segments.slice(0, -1).forEach((segment) => {
        const next = node[segment];
        if (next === null || typeof next !== 'object' || Array.isArray(next))
            node[segment] = {};
        node = node[segment];
    });
    const last = segments[segments.length - 1];
    if (value === undefined)
        delete node[last];
    else
        node[last] = JSON.parse(JSON.stringify(value));
}
const clone = (value) => JSON.parse(JSON.stringify(value));
/**
 * Recopie un préréglage dans une entrée de champ : les réglages du préréglage remplacent ceux de l'entrée,
 * sauf ceux déclarés « modifiés ici » (`overrides`), qui gardent leur valeur ; l'identifiant du champ, ses
 * calculs et ses non-conformités restent ; la citation passe à `ref` (`id@version` exacte). Après la recopie,
 * l'entrée ne dérive pas du préréglage (`presetDrift` vide), donc la gate de publication l'accepte.
 */
export function applyPresetToEntry(entry, preset, ref, overrides = []) {
    const next = clone(entry);
    for (const key of ENTRY_KEYS)
        delete next[key];
    const field = (entry['field'] ?? {});
    next['field'] = { ...clone(preset.field), field_id: field['field_id'] };
    const declared = (preset.entry ?? {});
    for (const key of ENTRY_KEYS)
        if (declared[key] !== undefined)
            next[key] = clone(declared[key]);
    const presentation = (next['presentation'] ?? {});
    if (presentation['widget'] === undefined)
        next['presentation'] = { ...presentation, widget: preset.widget };
    for (const pointer of overrides) {
        if (pointer === '/field/field_id' || pointer.startsWith('/preset'))
            continue;
        writeAtPointer(next, pointer, valueAtPointer(entry, pointer));
    }
    next['preset'] = overrides.length === 0 ? { ref } : { ref, overrides: [...overrides] };
    return next;
}
/**
 * Nouveau corps d'un gabarit après le changement d'un préréglage : chaque champ qui cite l'un de `presetIds`
 * reçoit le nouveau réglage, sauf ses surcharges, et cite `ref`. `null` si le gabarit ne cite pas le préréglage.
 */
export function propagatePreset(template, presetIds, ref, current, proposed) {
    const changed = current === null ? null : (() => {
        const before = presetProjection(current);
        const after = presetProjection(proposed);
        return [...new Set([...before.keys(), ...after.keys()])].filter((pointer) => before.get(pointer) !== after.get(pointer));
    })();
    const fields = [];
    const body = clone(template);
    for (const section of body.sections) {
        section.fields = section.fields.map((entry) => {
            const preset = entry['preset'];
            if (preset === undefined)
                return entry;
            const at = preset.ref.lastIndexOf('@');
            if (!presetIds.includes(preset.ref.slice(0, at)))
                return entry;
            const overrides = preset.overrides ?? [];
            const kept = changed === null ? [] : changed.filter((pointer) => covered(pointer, overrides));
            const outcome = changed === null ? 'follows' : changed.length === 0 ? 'unchanged' : kept.length === changed.length ? 'keeps' : 'follows';
            fields.push({ section_id: section.section_id, field_id: String(entry['field']['field_id']), outcome });
            return applyPresetToEntry(entry, proposed, ref, overrides);
        });
    }
    return fields.length === 0 ? null : { body: body, fields };
}
//# sourceMappingURL=form-editor-check.js.map